import type { SVGProps } from 'react';

type IconName =
  | 'arrow-left'
  | 'arrow-right'
  | 'arrow-down-double'
  | 'chevron-down'
  | 'chevron-up'
  | 'history'
  | 'book'
  | 'sparkles'
  | 'eye'
  | 'star'
  | 'user'
  | 'warning'
  | 'check'
  | 'close'
  | 'home'
  | 'cat'
  | 'heart'
  | 'coin'
  | 'compass'
  | 'moon'
  | 'one-card'
  | 'three-card'
  | 'cross-spread';

interface UiIconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: number;
}

function pathFor(name: IconName) {
  switch (name) {
    case 'arrow-left':
      return (
        <>
          <path d="M15 18l-6-6 6-6" />
          <path d="M9 12h10" />
        </>
      );
    case 'arrow-right':
      return (
        <>
          <path d="M9 6l6 6-6 6" />
          <path d="M15 12H5" />
        </>
      );
    case 'arrow-down-double':
      return (
        <>
          <path d="M7 8l5 5 5-5" />
          <path d="M7 13l5 5 5-5" />
        </>
      );
    case 'chevron-down':
      return <path d="M6 9l6 6 6-6" />;
    case 'chevron-up':
      return <path d="M6 15l6-6 6 6" />;
    case 'history':
      return (
        <>
          <path d="M3 12a9 9 0 1 0 3-6.7" />
          <path d="M3 4v5h5" />
          <path d="M12 8v5l3 2" />
        </>
      );
    case 'book':
      return (
        <>
          <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v16H6.5A2.5 2.5 0 0 0 4 21V5.5Z" />
          <path d="M8 7h8" />
          <path d="M8 11h8" />
        </>
      );
    case 'sparkles':
      return (
        <>
          <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3Z" />
          <path d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8L19 14Z" />
          <path d="M5 14l1 2.8L9 18l-3 .9L5 22l-1-3.1L1 18l3-.2L5 14Z" />
        </>
      );
    case 'eye':
      return (
        <>
          <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />
          <circle cx="12" cy="12" r="3" />
        </>
      );
    case 'star':
      return (
        <path d="M12 3.8l2.2 4.4 4.8.7-3.5 3.4.8 4.8L12 15.7 7.7 17l.8-4.8L5 8.9l4.8-.7L12 3.8Z" />
      );
    case 'user':
      return (
        <>
          <circle cx="12" cy="8" r="3.5" />
          <path d="M5 20c1.1-3 3.8-4.8 7-4.8s5.9 1.8 7 4.8" />
        </>
      );
    case 'warning':
      return (
        <>
          <path d="M12 4l9 16H3l9-16Z" />
          <path d="M12 9v4" />
          <circle cx="12" cy="16.5" r=".8" fill="currentColor" stroke="none" />
        </>
      );
    case 'check':
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M8.5 12.5l2.4 2.4 4.6-5.1" />
        </>
      );
    case 'close':
      return (
        <>
          <path d="M6 6l12 12" />
          <path d="M18 6L6 18" />
        </>
      );
    case 'home':
      return (
        <>
          <path d="M4 11.5L12 5l8 6.5" />
          <path d="M6.5 10.5V19h11v-8.5" />
        </>
      );
    case 'cat':
      return (
        <>
          <path d="M7 8.5 5.5 5 4 9" />
          <path d="M17 8.5 18.5 5 20 9" />
          <path d="M6 14c0-4 2.8-6.5 6-6.5s6 2.5 6 6.5c0 3.6-2.6 6-6 6s-6-2.4-6-6Z" />
          <path d="M9.5 13.5h.01" />
          <path d="M14.5 13.5h.01" />
          <path d="M10 16c1.1.8 2.9.8 4 0" />
          <path d="M12 14v1.2" />
        </>
      );
    case 'heart':
      return (
        <path d="M12 20s-6.8-4.4-8.6-8.5C2 8.2 3.4 5.5 6.3 5.1c1.9-.3 3.2.7 4 2 1-1.3 2.8-2.3 4.9-2 2.7.4 4.5 3.1 3 6.4C16.4 15.6 12 20 12 20Z" />
      );
    case 'coin':
      return (
        <>
          <circle cx="12" cy="12" r="7.5" />
          <path d="M12 7.8v8.4" />
          <path d="M9.5 10.2c.6-.9 1.4-1.3 2.5-1.3 1.5 0 2.5.8 2.5 2 0 1.1-.8 1.7-2.5 2l-1 .2c-1.4.2-2.2.8-2.2 1.8 0 1.3 1.1 2.1 2.9 2.1 1.1 0 2-.3 2.7-1.1" />
        </>
      );
    case 'compass':
      return (
        <>
          <circle cx="12" cy="12" r="8.5" />
          <path d="M14.8 9.2 13 13l-3.8 1.8L11 11l3.8-1.8Z" />
        </>
      );
    case 'moon':
      return (
        <path d="M15.8 4.8a7.8 7.8 0 1 0 3.4 14.7A8.5 8.5 0 0 1 15.8 4.8Z" />
      );
    case 'one-card':
      return (
        <>
          <rect x="8" y="5" width="8" height="14" rx="1.5" />
          <path d="M12 8v8" />
          <path d="M10 10h4" />
        </>
      );
    case 'three-card':
      return (
        <>
          <rect x="3" y="7" width="5.2" height="10" rx="1" />
          <rect x="9.4" y="5" width="5.2" height="14" rx="1" />
          <rect x="15.8" y="7" width="5.2" height="10" rx="1" />
        </>
      );
    case 'cross-spread':
      return (
        <>
          <rect x="9" y="4" width="6" height="16" rx="1" />
          <rect x="4" y="9" width="16" height="6" rx="1" />
        </>
      );
      break;
    default:
      return null;
  }
}

function UiIcon({ name, className, size, ...props }: UiIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
      width={size}
      height={size}
      {...props}
    >
      {pathFor(name)}
    </svg>
  );
}

export { UiIcon };
export default UiIcon;
